<script lang="ts">
  import { onMount, createEventDispatcher } from 'svelte';

  import { FormGroup, InlineNotification } from 'carbon-components-svelte';
  import { Form } from '@mst-fe/sveltejs-forms';

  import type { InlineNotificationProps } from 'carbon-components-svelte/src/Notification/InlineNotification.svelte';

  import SlaTaskFormItems from './SlaTaskFormItems.svelte';
  import SlaAssociationsFormItems from './SlaAssociationsFormItems.svelte';

  import { getServerErrorMessage } from '../../../utils';
  import {
    buildSlaTaskSchema,
    getSlaTaskSubmitHandler,
    loadSlaTaskReferenceData,
    SlaTaskFormVariants,
    type ExistingSchedule,
    type SlaTaskFormVariant,
  } from './sla-task-form-shared';

  import type { DataFeedInventorySummaryRow } from '../../../services';
  import type { SlaTaskRow, SlaTaskFormValues } from '../../../../shared/types/sla-task-types';

  type SlaTaskFormInitialValues = Partial<SlaTaskFormValues> & { id?: string };

  export let variant: SlaTaskFormVariant = SlaTaskFormVariants.ADD,
    formSubmitButtonRef: HTMLElement | undefined = undefined,
    loading = false,
    initialValues: SlaTaskFormInitialValues = {};

  const dispatch = createEventDispatcher<{ submit: { action: string; data: SlaTaskRow } }>();

  const showTaskFields = variant !== SlaTaskFormVariants.EDIT_ASSOCIATIONS;
  const showAssociations = variant !== SlaTaskFormVariants.EDIT_TASK;

  let existingSchedules: ExistingSchedule[] = [],
    availableFeeds: DataFeedInventorySummaryRow[] = [],
    resultNotification: Required<Pick<InlineNotificationProps, 'title' | 'subtitle' | 'kind'>> | null = null;

  async function loadReferenceData(): Promise<void> {
    loading = true;

    try {
      const referenceData = await loadSlaTaskReferenceData({
        includeFeeds: showAssociations,
        includeSchedules:
          variant === SlaTaskFormVariants.ADD || variant === SlaTaskFormVariants.EDIT_TASK || variant === SlaTaskFormVariants.EDIT_ALL,
      });

      availableFeeds = referenceData.availableFeeds;
      existingSchedules = referenceData.existingSchedules;
    } catch (error) {
      console.error('[SlaTaskForm] Failed to load reference data!', error);
      resultNotification = {
        kind: 'error',
        title: 'Error:',
        subtitle: 'Failed to load feeds. Please try again later.',
      };
    } finally {
      loading = false;
    }
  }

  async function handleFormSubmission(event: CustomEvent): Promise<void> {
    const { values } = event.detail;

    resultNotification = null;
    loading = true;

    try {
      const { action, submit } = getSlaTaskSubmitHandler(variant, initialValues.id);
      const savedTask = await submit(values);

      dispatch('submit', { action, data: savedTask });
    } catch (error) {
      const errorMessage = getServerErrorMessage(error) ?? 'Verify your submission and try again.';
      console.error('[SlaTaskForm] Failed to submit!', error);

      resultNotification = {
        kind: 'error',
        title: 'Error:',
        subtitle: `Failed to submit! ${errorMessage}`,
      };
    } finally {
      loading = false;
    }
  }

  onMount(loadReferenceData);

  $: nearbyScheduleCandidates = existingSchedules.filter((schedule) => schedule.taskId !== initialValues.id);
  $: schema = buildSlaTaskSchema({
    includeTaskFields: showTaskFields,
    includeAssociations: showAssociations,
    existingSchedules,
    currentTaskId: initialValues.id,
  });
</script>

{#if resultNotification}
  <InlineNotification
    kind={resultNotification.kind}
    lowContrast
    title={resultNotification.title}
    subtitle={resultNotification.subtitle}
    hideCloseButton
  />
{/if}

<Form
  fieldDomString="input,select,textarea,div[name]"
  {initialValues}
  {schema}
  let:submitForm
  let:errors
  let:setValue
  let:touched
  let:values
  on:submit={handleFormSubmission}
>
  <FormGroup>
    {#if showTaskFields}
      <SlaTaskFormItems {errors} {touched} {values} {setValue} disabled={loading} existingSchedules={nearbyScheduleCandidates} />
    {/if}
    {#if showAssociations}
      <SlaAssociationsFormItems
        {errors}
        {values}
        {setValue}
        disabled={loading}
        {availableFeeds}
        initialAssociations={initialValues.associations ?? []}
      />
    {/if}
    <button hidden type="button" on:click={submitForm} bind:this={formSubmitButtonRef} />
  </FormGroup>
</Form>
